/**
 * `<FooterVersion />` — Sprint 16 S16-09.
 *
 * Muestra la versión del módulo activo según el pathname del browser.
 * Diagnóstico transversal: el usuario puede leer el footer y saber
 * exactamente qué hay en producción sin acceso a Render/Vercel.
 *
 * Comportamiento:
 * - Detecta módulo via `detectarModuloActivo(location.pathname)`.
 * - Llama `getVersion()` (cache stale-while-revalidate, S16-08).
 * - Si la versión del módulo es `null` (sin historia de deploys),
 *   muestra `—`.
 * - Click sobre el footer copia la versión completa al clipboard +
 *   muestra "copiado" durante 2 segundos.
 *
 * Estilo: text-xs text-muted, font-mono para el sha. Discreto, no
 * compite con el branding del Footer.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  detectarModuloActivo,
  getVersion,
  versionDeModulo,
  type ModuloFuncional,
  type VersionResponse,
} from '../../lib/api/version';

const ETIQUETA_MODULO: Record<ModuloFuncional, string> = {
  portal: 'Portal',
  administracion: 'Administración',
  tesoreria: 'Tesorería',
  comision: 'Comisión',
};

interface FooterVersionProps {
  /** Override del pathname para tests y previews. Default: useLocation(). */
  pathnameOverride?: string;
}

export function FooterVersion({ pathnameOverride }: FooterVersionProps) {
  const location = useLocation();
  const pathname = pathnameOverride ?? location.pathname;
  const modulo = useMemo<ModuloFuncional>(
    () => detectarModuloActivo(pathname),
    [pathname],
  );
  const [data, setData] = useState<VersionResponse | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    let cancelado = false;
    getVersion()
      .then((r) => {
        if (!cancelado) setData(r);
      })
      .catch(() => {
        /* silencioso — el footer simplemente muestra `—` */
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const versionString = data ? versionDeModulo(data, modulo) : null;
  const textoMostrar = versionString ?? '—';

  async function handleClick() {
    if (!versionString) return;
    const completo = `${ETIQUETA_MODULO[modulo]} · ${versionString}`;
    try {
      await navigator.clipboard.writeText(completo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* clipboard no disponible — silencioso */
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!versionString}
      className="text-xs text-text-muted hover:text-text-secondary transition-colors text-left font-mono"
      aria-label={`Versión del módulo ${ETIQUETA_MODULO[modulo]}`}
      title={
        versionString
          ? 'Click para copiar al clipboard'
          : 'Versión no disponible'
      }
    >
      <span className="text-text-secondary font-sans">
        {ETIQUETA_MODULO[modulo]}
      </span>
      <span className="opacity-60 mx-1">·</span>
      <span>{textoMostrar}</span>
      {copiado && (
        <span className="ml-2 text-state-success font-sans">copiado</span>
      )}
    </button>
  );
}
